<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end">
        <v-text-field
          :value="searchRecipe"
          label="Busca subrecetas por nombre"
          append-icon="mdi-magnify"
          solo
          rounded
          hide-details
          flat
          dense
          @input="search"
        />
        <div class="mx-1">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                depressed
                fab
                x-small
                @click="openExportRecipes"
                color="primary">
                  <v-icon small color="white">mdi-application-export</v-icon>
              </v-btn>
            </template>
            <span>Exportar subrecetas</span>
          </v-tooltip>
        </div>
        <div class="mx-1">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                depressed
                fab
                color="secondary"
                x-small
                :to="{ name: 'EditSubrecipes' }"
              >
                <v-icon
                  small
                  color="white"
                >
                  mdi-forwardburger
                </v-icon>
              </v-btn>
            </template>
            <span>Editar múltiples subrecetas</span>
          </v-tooltip>
        </div>
        <div class="mx-1">
          <NewRecipe :subrecipe="true" @close="load"/>
        </div>
      </div>
    </div>

    <v-flex xs12>
      <v-skeleton-loader
        v-if="overlay"
        type="table"
      />
      <v-data-table
        v-else
        :loading-text="table.title"
        :loading="table.loading"
        :server-items-length="tableTotal"
        :headers="table.headers"
        :items="tableData"
        :options="tableOptions"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:item.name="{ item }">
          <a
            v-if="$is() === 'manager' || $is() === 'chef'"
            href="" @click.prevent="openUpdateIngredientsDialog(item)">
            {{ item.name }}
          </a>
          <span v-else>
            {{ item.name }}
          </span>
        </template>
        <template v-slot:item.restaurant="{ item }">
          {{ item.restaurant ? item.restaurant.name : '' }}
        </template>
        <template v-slot:item.production="{ item }">
          <div class="text-right">
            <span v-if="item.production">
              {{ item.production | toFixNumber(2) }} {{ item.unitStock.code }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.cost="{ item }">
          <div class="text-right">
            ${{ item.cost | toFix2 | numberWithComma }}
          </div>
        </template>
        <!-- <template v-slot:item.unitCost="{ item }">
          <div class="text-right">
            <div v-if="item.unitCost">
              <div>
                ${{ item.unitCost | toFix2 | numberWithComma }}
              </div>
              <div class="caption grey--text">
                1 / {{ item.unitStock.code }}
              </div>
            </div>
            <div v-else>
              -
            </div>
          </div>
        </template> -->
        <template v-slot:item.status="{ item }">
          <template>
            <div :class="`${statusColors[item.status]}--text`">
              {{ convertStatus(item.status)['text'] }}
            </div>
          </template>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end">
            <span
              v-if="$is() === 'manager' || $is() === 'chef'"
              class="mx-2">
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    depressed
                    fab
                    x-small
                    :href="exportPDF(item)"
                    target="_blank"
                    color="#26A69A">
                      <v-icon small color="white">mdi-file-document</v-icon>
                  </v-btn>
                </template>
                <span>Exportar PDF</span>
              </v-tooltip>
            </span>
            <span class="mr-2">
              <AttachRecipe
                v-if="item.status === 'in-progress'
                  && $store.state.currentRestaurant.data._id
                  && ($is() === 'manager' || $is() === 'chef')
                  && item.production > 0"
                :key="`attach-${item.uuid}`"
                :item="item"
                @close="load"
              />
            </span>
            <span class="mr-2">
              <DuplicateRecipe
                v-if="$is() === 'manager' || $is() === 'chef'"
                :key="`duplicate-${item.uuid}`"
                :item="item"
              />
            </span>
            <span class="mr-2">
              <EditRecipe
                v-if="$is() === 'manager' || $is() === 'chef'"
                :key="`edit-${item.uuid}`"
                :item="item"
                @close="load"
              />
            </span>
            <v-tooltip
              v-if="$is() === 'manager' || $is() === 'chef'"
              left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  fab
                  x-small
                  depressed
                  color="error"
                  v-on="on"
                  @click="openRemoveRecipeDialog(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar receta</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog @keydown.esc="confirmRemoveSubrecipeDialog=false" v-model="confirmRemoveSubrecipeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('subrecipes.dependencies') }}</span>
        </v-card-title>
        <v-card-text>{{ confirmRemoveSubrecipeMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRemoveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="remove(true)">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="removeRecipeDialog=false" v-model="removeRecipeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Eliminar la subreceta {{ form.data.name }}?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRemoveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="remove(false)">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <IngredientsDialog
      :recipe="recipe"
      :ingredientDialog="ingredientDialog"
      @closeIngredientDialog="closeIngredientDialog"
    />

    <ExportRecipes ref="exportRecipes" :subRecipe="true"/>
  </div>
</template>

<script>
import { debounce, isEqual } from 'lodash'
import IngredientsDialog from '@/components/sidePanels/RecipeDetail'
import NewRecipe from '@/components/modals/NewRecipe'
import EditRecipe from '@/components/modals/EditRecipe'
import DuplicateRecipe from '@/components/modals/DuplicateRecipe'
import AttachRecipe from '@/components/modals/AttachRecipe'
import ExportRecipes from '@/components/modals/ExportRecipesCsv'

export default {
  name: 'Subrecipes',
  components: {
    IngredientsDialog,
    NewRecipe,
    EditRecipe,
    AttachRecipe,
    DuplicateRecipe,
    ExportRecipes
  },
  data () {
    return {
      firstLoad: false,
      recipeFormDialog: false,
      removeRecipeDialog: false,
      ingredientDialog: false,
      confirmRemoveSubrecipeMessage: '',
      confirmRemoveSubrecipeDialog: false,
      section: this.$i18n.t('subrecipes.section'),
      recipe: {
        uuid: null
      },
      table: {
        title: this.$i18n.t('subrecipes.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('subrecipes.headers.name'), value: 'name' },
          { text: this.$i18n.t('subrecipes.headers.production'), value: 'production', sortable: false },
          { text: this.$i18n.t('subrecipes.headers.cost'), value: 'cost', sortable: false },
          { text: this.$i18n.t('subrecipes.headers.status'), value: 'status', sortable: false },
          { text: this.$i18n.t('subrecipes.headers.actions'), value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: []
        }
      },
      uploadForm: {
        valid: false,
        data: {
          subrecipes: null
        },
        rules: {
          subrecipes: [v => !!v || this.$i18n.t('subrecipes.labels.uploadACsvFile')]
        }
      },
      form: {
        valid: false,
        search: '',
        meta: {
          unitStock: { disabled: true },
          subrecipe: { disabled: false }
        },
        data: {
          name: null,
          status: null,
          subrecipe: true,
          restaurant: null,
          production: null,
          unitStock: null,
          area: null
        }
      },
      status: [
        { text: 'En elaboración', value: 'in-progress' },
        { text: 'Listado como producto', value: 'attached' },
        { text: 'No adjuntado', value: 'unattached' },
        { text: 'Archivado', value: 'archived' }
      ],
      statusColors: {
        'in-progress': 'grey',
        'attached': 'primary',
        'unattached': 'danger',
        'archived': 'bluegrey'
      },
      name: ''
    }
  },
  computed: {
    tableOptions () {
      return this.table.options
    },
    tableTotal () {
      return this.table.total
    },
    tableData () {
      return this.table.data
    },
    overlay () {
      return this.firstLoad
    },
    searchRecipe () {
      return this.name
    }
  },
  watch: {
    '$store.state.area': async function (val) {
      await this.load()
    }
  },
  async mounted () {
    this.firstLoad = true
    await this.load()
    this.firstLoad = false
  },
  methods: {
    exportPDF (item) {
      let url = process.env.VUE_APP_API_URL
      url += `/v1/recipes/${item._id}/download-pdf`
      url += `?subrecipe=true`
      return url
    },
    openExportRecipes () {
      this.$refs.exportRecipes.open()
    },
    search: debounce(async function (val) {
      this.name = val
      await this.load()
    }, 300),
    async closeIngredientDialog () {
      this.ingredientDialog = false
    },
    convertStatus (key) {
      return this.status.find(stat => stat.value === key) || ''
    },
    updateTable: async function (data) {
      const options = this.table.options
      if (!isEqual(options, data)) {
        this.table.options = data
        await this.load()
      }
    },
    async load (params = null) {
      this.table.loading = true
      try {
        params = {
          limit: this.table.options.itemsPerPage,
          skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
          sort: this.table.options.sortBy[0],
          area: this.$store.state.area.id,
          subrecipe: true,
          status: (this.statusSelected || {}).value,
          restaurant: this.$store.state.currentRestaurant.data.uuid
        }

        if (this.name.length >= 3) {
          params.name = `/${this.name}/i`
        }

        const recipes = await this.$api.get('/v1/recipes', params)
        this.table.data = recipes.data
        this.table.total = recipes.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    onCloseRemoveDialog: function () {
      this.removeRecipeDialog = false
      this.confirmRemoveSubrecipeDialog = false
    },
    openUpdateIngredientsDialog: async function (recipe) {
      this.form.data = recipe
      this.recipe = recipe
      this.ingredientDialog = true
    },
    openRemoveRecipeDialog: async function (recipe) {
      this.form.data = recipe
      this.removeRecipeDialog = true
    },
    remove: async function (force) {
      this.overlay = true
      try {
        await this.$api.post(`/v1/recipes/${this.form.data.uuid}/delete`)
        await this.load()
        this.onCloseRemoveDialog()
      } catch (error) {
        if (error.status === 423) {
          this.confirmRemoveSubrecipeMessage = error.message
          this.confirmRemoveSubrecipeDialog = true
        } else {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      }

      this.overlay = false
    },
    getSubrecipeColor (isSubrecipe) {
      return isSubrecipe ? 'teal darken-4' : 'teal lighten-5'
    },
    getSubrecipeTextColor (isSubrecipe) {
      return isSubrecipe ? 'teal lighten-5' : 'teal darken-4'
    }
  }
}
</script>
